import Vue from "vue";
import createAuth0Client from "@auth0/auth0-spa-js";
import Router from '../router';

const DEFAULT_REDIRECT_CALLBACK = () =>
  window.history.replaceState({}, document.title, window.location.pathname);

let instance;

export const getInstance = () => instance;

export const useAuth0 = ({
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  redirectUri = window.location.origin,
  ...options
}) => {
  if (instance) return instance;

  instance = new Vue({
    data() {
      return {
        loading: true,
        isAuthenticated: false,
        user: {},
        auth0Client: null,
        popupOpen: false,
        error: null,
        scope: options.scope,

        expiryTime: null,
        expiryInterval: null,
      };
    },
    mounted() {   },
    methods: {
      async loginWithPopup(o) {
        this.popupOpen = true;

        try {
          await this.auth0Client.loginWithPopup(o);
          this.user = await this.auth0Client.getUser();
          this.isAuthenticated = await this.auth0Client.isAuthenticated();
          this.error = null;
        } catch (e) {
          console.error(e);
          this.error = e;
        } finally {
          this.popupOpen = false;
        }
      },
      onVisibilityChange() {
        if (!document.hidden) {
          this.checkExpiry();
        }
      },
      async expireSessionCallbackGenerator(secondsTilExpiry = 36000) {
        const expiryMs = secondsTilExpiry * 1000;
        const iatMs = (await this.getIdTokenClaims()).iat * 1000;
        this.expiryTime = new Date(iatMs + expiryMs);
        console.log('Registering Expiry', this.expiryTime);
        window.addEventListener('focus', this.onVisibilityChange.bind(this));
        this.expiryInterval = setInterval(() => {
          this.checkExpiry();
        }, 2000);
      },
      async checkExpiry() {
        if (this.expiryTime <= new Date()) {
          try {
            this.isAuthenticated = false;
            await this.logout({ localOnly: true });
            Router.replace({ name: 'loggedout' });
          } catch { } 
          clearInterval(this.expiryInterval);
        }
      },
      async handleRedirectCallback() {
        this.loading = true;
        try {
          await this.auth0Client.handleRedirectCallback();
          this.user = await this.auth0Client.getUser();
          this.isAuthenticated = true;
          this.error = null;
        } catch (e) {
          this.error = e;
        } finally {
          this.loading = false;
        }
      },
      loginWithRedirect(o) {
        return this.auth0Client.loginWithRedirect(o);
      },
      async getIdTokenClaims(o) {
        try {
          const claims = await this.auth0Client.getIdTokenClaims(o);
          return claims || {};
        } catch (e) {
          return {};
        }
      },
      getTokenSilently(o) {
        return this.auth0Client.getTokenSilently(o);
      },
      getTokenWithPopup(o) {
        return this.auth0Client.getTokenWithPopup(o);
      },
      logout(o) {
        return this.auth0Client.logout(o);
      }
    },
    async created() {
      this.auth0Client = await createAuth0Client({
        domain: options.domain,
        client_id: options.clientId,
        audience: options.audience,
        redirect_uri: redirectUri,
        connection: this.scope,
      });
      try {
        if (
          window.location.search.includes("code=") &&
          window.location.search.includes("state=")
        ) {
          const { appState } = await this.auth0Client.handleRedirectCallback();
          this.error = null;
          onRedirectCallback(appState);
        }
      } catch (e) {
        this.error = e;
      } finally {
        this.isAuthenticated = await this.auth0Client.isAuthenticated();
        if (this.isAuthenticated) {
          this.expireSessionCallbackGenerator();
        }
        this.user = await this.auth0Client.getUser();
        this.loading = false;
      }
    }
  });

  return instance;
};

export const Auth0Plugin = {
  install(Vue, options) {
    Vue.prototype.$auth = useAuth0(options);
  }
};
